<script setup lang="ts">
import { useStoreUserV2 } from "~/stores/storeUserV2";
import Currency from "~/common/enums/Currency";
import { getCurrencySymbol } from "~/utils/currency";
import { useI18n } from "vue-i18n";
import { useId } from "vue";
import { DollarSign, Euro } from "lucide-vue-next";

const { t } = useI18n();
const id = useId();

const userStore = useStoreUserV2();

const availableCurrencies = [
  { key: Currency.USD, label: getCurrencySymbol(Currency.USD) },
  { key: Currency.WMATIC, label: getCurrencySymbol(Currency.WMATIC) },
  { key: Currency.EUR, label: getCurrencySymbol(Currency.EUR) },
];

const availableCurrenciesFiltered = availableCurrencies.filter((currency) => {
  // If user is migrated from wPOL-USDC, hide USD option
  if (currency.key === Currency.WMATIC && userStore.migrationToUSDCCompleted) {
    return false;
  }
  return true;
});

const handleUpdatePreferredCurrency = (currency: string) => {
  userStore.updatePreferredCurrency(currency as Currency);
};
</script>

<template>
  <div>
    <div class="bg-slate-900 p-3 text-sm text-slate-400 mb-0.5 px-5 rounded-t-lg">
      {{ t("drawers.profile.settingsView.preferences.preferredCurrency", "Preferred currency") }}
    </div>
    <div
      class="grid gap-2 bg-slate-900 p-5 py-5 rounded-b-lg"
      :class="`grid-cols-${availableCurrenciesFiltered.length}`"
    >
      <button
        v-for="currency in availableCurrenciesFiltered"
        class="rounded-lg border border-transparent hover:bg-slate-800 bg-slate-950 py-2 px-4 flex flex-col items-center justify-center gap-2 pt-2.5"
        :class="{
          ' border !border-primary-60 !w-auto bg-primary-80/10':
            userStore.preferredCurrency === currency.key,
        }"
        @click="handleUpdatePreferredCurrency(currency.key)"
        tabindex="-1"
      >
        <DollarSign v-if="currency.key === Currency.USD" class="h-5" />
        <icon-fg-w-pol v-else-if="currency.key === Currency.WMATIC" class="h-[19px]" />
        <Euro v-else-if="currency.key === Currency.EUR" class="h-5" />
        <span
          class="text-xs text-slate-400"
          :class="{
            'text-white': userStore.preferredCurrency === currency.key,
          }"
        >
          <template v-if="currency.key === Currency.USD"> USD </template>
          <template v-else-if="currency.key === Currency.WMATIC"> WPOL </template>
          <template v-else-if="currency.key === Currency.EUR"> EUR </template>
        </span>
      </button>
    </div>
  </div>
</template>
